import React, { useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../../config';
import './PostalCodeDataUpload.css';

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

const PostalCodeDataUpload: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const canUpload = Boolean(file) && !busy;

  const fileHint = useMemo(() => {
    if (!file) return '未選択';
    return `${file.name}（${formatBytes(file.size)}）`;
  }, [file]);

  const onPickFile = (f: File | null) => {
    setMessage(null);
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/postal-code-data/upload`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        let detail = '';
        try {
          const data = await res.json();
          if (data && typeof data.message === 'string') detail = data.message;
        } catch {
          /* ignore */
        }
        throw new Error(detail || `アップロードに失敗しました（${res.status}）。`);
      }
      setMessage({ type: 'success', text: 'アップロードが完了しました。' });
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e) {
      const text = e instanceof Error ? e.message : 'アップロードに失敗しました。';
      setMessage({ type: 'error', text });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pcdu">
      <div className="pcdu__header">
        <p className="pcdu__intro">
          従業員の住所入力で使用する <strong>郵便番号→住所</strong> のデータを更新します。
          <br />
          日本郵便が提供する CSV をダウンロードして、この画面からアップロードしてください。
        </p>
      </div>

      <section className="pcdu__card">
        <h2 className="pcdu__card-title">1) CSV をダウンロード</h2>
        <ol className="pcdu__steps">
          <li>
            日本郵便の「郵便番号データダウンロード」にアクセス：
            <a
              className="pcdu__link"
              href="https://www.post.japanpost.jp/zipcode/download.html"
              target="_blank"
              rel="noreferrer"
            >
              https://www.post.japanpost.jp/zipcode/download.html
            </a>
          </li>
          <li>
            <strong>「住所の郵便番号（1レコード1行、UTF-8形式）（CSV形式）」</strong> をダウンロードします。
          </li>
          <li>ダウンロードしたファイル（例：`utf_ken_all.csv`）を保存します。</li>
        </ol>
        <p className="pcdu__note">
          ※ 住所が複数候補になる郵便番号もあるため、データ更新は定期的に行うことを推奨します。
        </p>
      </section>

      <section className="pcdu__card">
        <h2 className="pcdu__card-title">2) CSV をアップロード</h2>

        <div className="pcdu__upload-row">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="pcdu__file"
            onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            disabled={busy}
          />
          <button type="button" className="pcdu__btn" onClick={() => inputRef.current?.click()} disabled={busy}>
            ファイルを選択
          </button>
          <button type="button" className="pcdu__btn pcdu__btn--primary" onClick={() => void handleUpload()} disabled={!canUpload}>
            {busy ? 'アップロード中…' : 'アップロード'}
          </button>
          <span className="pcdu__upload-hint">※ 数分かかる場合があります</span>
        </div>

        <div className="pcdu__file-hint">
          選択中：<span className="pcdu__file-name">{fileHint}</span>
        </div>

        {message && (
          <p className={message.type === 'success' ? 'pcdu__msg pcdu__msg--ok' : 'pcdu__msg pcdu__msg--err'} role="alert">
            {message.text}
          </p>
        )}

        <p className="pcdu__note">
          ※ アップロード後、システム内の郵便番号検索が新しいデータに更新されます。
        </p>
      </section>
    </div>
  );
};

export default PostalCodeDataUpload;

